import { createDefaultWidgetPage, DB_VERSION, DEFAULT_WIDGET_PAGE_ID, getDB, normalizeWidgetPageId } from './core';
import type { AppSettings, DataItem, FolderItem, FolderDataLink, WidgetItem, TagItem, WidgetPage } from '@/types';

export interface BackupData {
  data: DataItem[];
  folders: FolderItem[];
  folderLinks: FolderDataLink[];
  widgets: WidgetItem[];
  widgetPages?: WidgetPage[];
  tags: TagItem[];
  settings: AppSettings[];
  version: number;
  exportedAt: number;
}

/**
 * Exports all important data (excluding history) as a compressed binary Blob.
 */
export async function exportStorageData(onProgress?: (step: string, progress: number) => void): Promise<Blob> {
  const db = await getDB();
  
  onProgress?.('collecting', 10);
  const [data, folders, folderLinks, widgets, widgetPages, tags, settings] = await Promise.all([
    db.getAll('data'),
    db.getAll('folders'),
    db.getAll('folderLinks'),
    db.getAll('widgets'),
    db.getAll('widgetPages'),
    db.getAll('tags'),
    db.getAll('settings'),
  ]);

  const backup: BackupData = {
    data,
    folders,
    folderLinks,
    widgets,
    widgetPages,
    tags,
    settings,
    version: DB_VERSION,
    exportedAt: Date.now(),
  };

  onProgress?.('compressing', 50);
  const jsonString = JSON.stringify(backup);
  const stream = new Blob([jsonString]).stream();
  const compressedStream = stream.pipeThrough(new CompressionStream('gzip'));
  
  const chunks: Uint8Array[] = [];
  const reader = compressedStream.getReader();
  
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }

  onProgress?.('finalizing', 90);
  const blobParts = chunks.map((chunk) => {
    const copy = new Uint8Array(chunk.byteLength);
    copy.set(chunk);
    return copy.buffer;
  });
  return new Blob(blobParts, { type: 'application/x-mfc-backup' });
}

/**
 * Imports data from a compressed binary Blob, replacing existing data.
 */
export async function importStorageData(blob: Blob, onProgress?: (step: string, progress: number) => void): Promise<void> {
  onProgress?.('decompressing', 10);
  const decompressedStream = blob.stream().pipeThrough(new DecompressionStream('gzip'));
  const response = new Response(decompressedStream);
  const jsonString = await response.text();
  const backup: BackupData = JSON.parse(jsonString);

  if (backup.version > DB_VERSION) {
    throw new Error('Backup version is newer than current app version.');
  }

  onProgress?.('restoring', 40);
  const db = await getDB();
  const tx = db.transaction(['data', 'folders', 'folderLinks', 'widgets', 'widgetPages', 'tags', 'settings', 'history'], 'readwrite');

  const widgetPages = backup.widgetPages && backup.widgetPages.length > 0
    ? backup.widgetPages
    : [createDefaultWidgetPage()];
  const pageIds = new Set(widgetPages.map((page) => page.id));
  const fallbackPageId = pageIds.has(DEFAULT_WIDGET_PAGE_ID) ? DEFAULT_WIDGET_PAGE_ID : widgetPages[0].id;
  const widgets = backup.widgets.map((widget) => {
    const normalized = normalizeWidgetPageId(widget);
    return pageIds.has(normalized.pageId)
      ? normalized
      : { ...normalized, pageId: fallbackPageId };
  });

  // Clear all stores
  await Promise.all([
    tx.objectStore('data').clear(),
    tx.objectStore('folders').clear(),
    tx.objectStore('folderLinks').clear(),
    tx.objectStore('widgets').clear(),
    tx.objectStore('widgetPages').clear(),
    tx.objectStore('tags').clear(),
    tx.objectStore('settings').clear(),
    tx.objectStore('history').clear(), // Clear history too even if not imported
  ]);

  onProgress?.('restoring', 60);
  // Restore data
  await Promise.all([
    ...backup.data.map(item => tx.objectStore('data').put(item)),
    ...backup.folders.map(item => tx.objectStore('folders').put(item)),
    ...backup.folderLinks.map(item => tx.objectStore('folderLinks').put(item)),
    ...widgetPages.map(item => tx.objectStore('widgetPages').put(item)),
    ...widgets.map(item => tx.objectStore('widgets').put(item)),
    ...backup.tags.map(item => tx.objectStore('tags').put(item)),
    ...backup.settings.map(item => tx.objectStore('settings').put(item)),
  ]);

  await tx.done;
  onProgress?.('done', 100);
}
